import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * One number, and enough around it to know whether to believe it.
 *
 * `sample` IS NOT DECORATION. "31.2% margin" over four quotes and over four hundred render
 * identically, and only one of them is a fact about the business. Where a figure is a mean, the
 * tile says what it is a mean of.
 *
 * `live` marks the tiles that are true now rather than as of the last refresh. The dashboard mixes
 * both deliberately — see DashboardSummary — so it has to say which is which somewhere, and the
 * tile is where a reader is actually looking.
 */
export function KpiTile({
  label,
  value,
  hint,
  sample,
  live = false,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  sample?: string;
  live?: boolean;
  emphasis?: boolean;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
          >
            {label}
          </Typography>

          {hint && (
            <Tooltip title={hint}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Tooltip>
          )}

          {live && (
            <Tooltip title="Live — counted at the moment you loaded this page, not from the hourly rebuild.">
              <Box
                aria-label="live"
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', ml: 0.25 }}
              />
            </Tooltip>
          )}
        </Stack>

        <Typography
          variant={emphasis ? 'h1' : 'h2'}
          sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}
        >
          {value}
        </Typography>

        {sample && (
          <Typography variant="caption" color="text.secondary">
            {sample}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
